import {
  http,
  DEFAULT_TOKEN,
  getAuthToken,
  type ExtendedAxiosRequestConfig,
} from './client';
import { useConfig } from '../hooks/useConfig';

export type ScenarioTaskRecordStatus =
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface ScenarioTaskRecordItem {
  id: number;
  requestId: string | null;
  organizationId: string | null;
  namespace: string | null;
  ontologyName: string | null;
  ontologyId: number | null;
  organizationJson: string | null;
  opmJson?: string | null;
  parameters: string | Record<string, unknown> | null;
  resultText: string | null;
  status: ScenarioTaskRecordStatus;
  errorMessage: string | null;
  tokenUsage: number | null;
  durationMs: number | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ScenarioTaskRecordListResponse {
  items: ScenarioTaskRecordItem[];
  total: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface ScenarioTaskOntologyOption {
  namespace: string | null;
  ontologyId: number;
  ontologyName: string | null;
}

export interface GetScenarioTaskRecordListParams {
  status?: ScenarioTaskRecordStatus[];
  ontologyId?: number[];
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
}

export async function getScenarioTaskRecordList(
  params?: GetScenarioTaskRecordListParams
): Promise<ScenarioTaskRecordListResponse> {
  const response = await http.get<ScenarioTaskRecordListResponse>(
    '/ai-api/holon/v1/process-records',
    {
      params: {
        ...(params?.status?.length ? { status: params.status.join(',') } : {}),
        ...(params?.ontologyId?.length
          ? { ontologyId: params.ontologyId.join(',') }
          : {}),
        ...(params?.startDate ? { startDate: params.startDate } : {}),
        ...(params?.endDate ? { endDate: params.endDate } : {}),
        page: params?.page ?? 1,
        size: params?.size ?? 20,
      },
      useAuthToken: true,
    } as ExtendedAxiosRequestConfig
  );

  return (
    response.data || {
      items: [],
      total: 0,
      totalPages: 0,
      page: params?.page ?? 1,
      size: params?.size ?? 20,
    }
  );
}

export async function deleteScenarioTaskRecord(id: number): Promise<{
  deleted: number;
}> {
  const response = await http.delete<{ deleted: number }>(
    `/ai-api/holon/v1/process-records/${id}`,
    { useAuthToken: true } as ExtendedAxiosRequestConfig
  );

  return response.data || { deleted: 0 };
}

export async function getScenarioTaskOntologyList(): Promise<
  ScenarioTaskOntologyOption[]
> {
  const response = await http.get<ScenarioTaskOntologyOption[]>(
    '/ai-api/holon/v1/process-records/ontologies',
    { useAuthToken: true } as ExtendedAxiosRequestConfig
  );

  return response.data || [];
}

export interface CreateScenarioTaskPayload {
  组织ID: string;
  组织名称?: string;
  场景本体ID: number;
  场景本体名称?: string;
  对象本体ID?: number;
  对象本体名称?: string;
  知识库ID?: string;
  知识库名称?: string;
  用户问题: string;
  [key: string]: unknown;
}

export interface ScenarioTaskProcessLogEntry {
  type: string;
  category: string;
  name: string;
  content: string;
  processName?: string;
  history?: string;
  timestamp?: string;
}

function getHolonBaseUrl(): string {
  const { serverConfig } = useConfig();

  return (
    (serverConfig as { holonBaseUrl?: string })?.holonBaseUrl ||
    'https://holon.aialign.cn'
  ).replace(/\/$/, '');
}

export async function createScenarioTaskOrchestratorStream(
  payload: CreateScenarioTaskPayload,
  abortController?: AbortController
): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(
    `${getHolonBaseUrl()}/ai-api/holon/v1/processes/orchestrator`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
        Authorization: `Bearer ${DEFAULT_TOKEN}`,
      },
      body: JSON.stringify(payload),
      signal: abortController?.signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => '未知错误');
    throw new Error(`请求失败(${response.status})：${errorText}`);
  }

  if (!response.body) {
    throw new Error('响应体为空，无法读取流数据');
  }

  return response.body;
}

export async function getScenarioTaskRecordDetail(
  id: number | string
): Promise<ScenarioTaskRecordItem> {
  const response = await http.get<ScenarioTaskRecordItem>(
    `/ai-api/holon/v1/process-records/${id}`
  );

  return response.data;
}

export async function getScenarioTaskRecordDetailByRequestId(
  requestId: string
): Promise<ScenarioTaskRecordItem> {
  const response = await http.get<ScenarioTaskRecordItem>(
    `/ai-api/holon/v1/process-records/by-request/${requestId}`
  );

  return response.data;
}

export async function getScenarioTaskProcessLogsStream(
  requestId: string,
  abortController?: AbortController
): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(
    `${getHolonBaseUrl()}/ai-api/holon/v1/process-logs?requestId=${encodeURIComponent(requestId)}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/x-ndjson',
        Authorization: `Bearer ${DEFAULT_TOKEN}`,
      },
      signal: abortController?.signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => '未知错误');
    throw new Error(`请求失败(${response.status})：${errorText}`);
  }

  if (!response.body) {
    throw new Error('响应体为空，无法读取流数据');
  }

  return response.body;
}
